import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { config } from "../../shared/environment";
import { RootState } from "../store";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { ZenToast } from "zen-core-elements";

type buyXgetYOfferResponse = {
    buyXGetYOfferReport: {
        count: string | number
        buyXgetYDiscount: {
            count?: number
            orderCount?: number
            title: string
            totalAmount?: number
            discountAmount?: number
        }[]
    }
    statusCode: string
    message: string
    success: boolean
}

type buyXgetFreeResponse = {
    payXGetFreeOfferReport: {
        count: string | number
        payXGetFreeDiscounts: {
            count?: number
            orderCount?: number
            title: string
            totalAmount?: number
            discountAmount?: number
        }[]
    }
    statusCode: string
    message: string
    success: boolean
}

export type manualOfferResponse = {
    manualOfferReport: {
        count: number | string,
        manualDiscount:
        {
            promoCode: string,
            count: number,
            totalAmount: number
        }[]
    },
    statusCode: string;
    message: string;
    success: boolean;
};

export type automaticOfferResponse = {
    automaticOfferReport: {
        count: number | string,
        automaticDiscounts:
        {
            title: string,
            orderCount: number,
            discountAmount: number
        }[]
    },
    statusCode: string;
    message: string;
    success: boolean;
};


export type additionalOfferResponse = {
    additionalDiscountOfferReport: {
        count: number | string,
        additionalDiscount:
        {
            date: string,
            orderCount: number,
            totalAmount: number
        }[]
    },
    statusCode: string;
    message: string;
    success: boolean;
};

export type loyaltyOfferResponse = {
    loyaltyOfferReport: {
        count: number | string,
        loyaltyDiscount:
        {
            date: string,
            orderCount: number,
            totalAmount: number
        }[]
    },
    statusCode: string;
    message: string;
    success: boolean;
};

export type filterData = { from?: string | null; to?: string | null }
export type offers = "manualOffer" | "automaticOffer" | "additionalOffer" | "loyaltyOffer" | "buyXGetYOffer" | "payXGetFreeOffer"
export type FlattenedReportType = {
    title: string,
    orderCount: number,
    totalAmountPerCodeOrDate: number
};

type offerReportInitialState = {
    offer: {
        [key in offers]?: {
            value: FlattenedReportType[]
            offset: number
            count: number
            filterData: filterData
            loading: boolean
            totalOfferAmount: number
        }
    }
    selectedOffer: offers
}

const offerReportInitialState: offerReportInitialState = {
    offer: {},
    selectedOffer: "manualOffer"
}

export const offersReport = createAsyncThunk('offerReport/offersReport', ({ data }: { data: { limit: number, offset: number } }, { getState }) => {
    const { offerReport } = getState() as RootState
    const selectedOffer = offerReport?.selectedOffer
    const date = offerReport?.offer[selectedOffer]?.filterData!!
    let Url = HttpRoutingService.replaceUrl(API?.offerReport + selectedOffer, { storeId: config.storeId })
    return HttpRoutingService.getMethod<manualOfferResponse & automaticOfferResponse & loyaltyOfferResponse & additionalOfferResponse & buyXgetYOfferResponse & buyXgetFreeResponse>(Url, {
        ...data,
        filterData: JSON.stringify({ customDateRange: { begin: date?.from || null, end: date?.to || null } })
    })
})

const offerReportSlice = createSlice({
    initialState: offerReportInitialState,
    name: "offerReport",
    reducers: {
        setOffer(state, action: PayloadAction<offers>) {
            state.selectedOffer = action.payload
        },
        setFilterData(state, action: PayloadAction<filterData>) {
            let selectedOffer = state.selectedOffer
            if (!state.offer[selectedOffer]) {
                state.offer[selectedOffer] = {
                    value: [],
                    offset: 0,
                    count: 0,
                    filterData: action.payload,
                    loading: false,
                    totalOfferAmount: 0
                }
            } else if (state.offer[selectedOffer]?.filterData) {
                state.offer[selectedOffer]!.filterData = { ...state.offer[selectedOffer]!.filterData, ...action?.payload }
            } else {
                state.offer[selectedOffer]!.filterData = action.payload
            }
        }
    },
    extraReducers(builder) {
        builder
            .addCase(offersReport.pending, (state) => {
                const selectedOffer = state?.selectedOffer
                state.offer[selectedOffer] = { ...state.offer[selectedOffer]!!, loading: true }
            })
            .addCase(offersReport.fulfilled, (state, action) => {
                const selectedOffer = state?.selectedOffer
                let { data: { offset } } = action.meta.arg;
                let prev = state.offer?.[selectedOffer]
                const date = prev?.filterData
                let current: FlattenedReportType[] = []
                let count: number = 0
                //manual
                if (action?.payload?.manualOfferReport) {
                    count = Number(action?.payload?.manualOfferReport?.count || 0)
                    current = action?.payload?.manualOfferReport?.manualDiscount?.map(d => ({
                        title: d.promoCode,
                        orderCount: Number(d.count || 0),
                        totalAmountPerCodeOrDate: Number(d.totalAmount || 0)
                    })) || []
                }
                //automatic
                if (action?.payload?.automaticOfferReport) {
                    count = Number(action?.payload?.automaticOfferReport?.count || 0)
                    current = action?.payload?.automaticOfferReport?.automaticDiscounts?.map(d => ({
                        title: d.title,
                        orderCount: Number(d.orderCount || 0),
                        totalAmountPerCodeOrDate: Number(d.discountAmount || 0)
                    })) || []
                }
                //additional
                if (action?.payload?.additionalDiscountOfferReport) {
                    count = Number(action?.payload?.additionalDiscountOfferReport?.count || 0)
                    current = action?.payload?.additionalDiscountOfferReport?.additionalDiscount?.map(d => ({
                        title: d.date,
                        orderCount: Number(d.orderCount || 0),
                        totalAmountPerCodeOrDate: Number(d.totalAmount || 0)
                    })) || []
                }
                //loyalty
                if (action?.payload?.loyaltyOfferReport) {
                    count = Number(action?.payload?.loyaltyOfferReport?.count || 0)
                    current = action?.payload?.loyaltyOfferReport?.loyaltyDiscount?.map(d => ({
                        title: d.date,
                        orderCount: Number(d.orderCount || 0),
                        totalAmountPerCodeOrDate: Number(d.totalAmount || 0)
                    })) || []
                }
                //buyXgetY
                if (action?.payload?.buyXGetYOfferReport) {
                    count = Number(action?.payload?.buyXGetYOfferReport?.count || 0)
                    current = action?.payload?.buyXGetYOfferReport?.buyXgetYDiscount?.map(d => ({
                        title: d.title,
                        orderCount: Number(d.orderCount ?? d.count ?? 0),
                        totalAmountPerCodeOrDate: Number(d.discountAmount ?? d.totalAmount ?? 0)
                    })) || []
                }
                //buyXgetFree
                if (action?.payload?.payXGetFreeOfferReport) {
                    count = Number(action?.payload?.payXGetFreeOfferReport?.count || 0)
                    current = action?.payload?.payXGetFreeOfferReport?.payXGetFreeDiscounts?.map(d => ({
                        title: d.title,
                        orderCount: Number(d.orderCount ?? d.count ?? 0),
                        totalAmountPerCodeOrDate: Number(d.discountAmount ?? d.totalAmount ?? 0)
                    })) || []
                }

                if (offset > 0 && prev) {
                    const value = [...prev?.value, ...current]
                    const totalOfferAmount = value.reduce((acc, item) => acc + (Number(item?.totalAmountPerCodeOrDate) || 0), 0)
                    state.offer[selectedOffer] = {
                        value,
                        count,
                        filterData: { from: date?.from, to: date?.to },
                        offset,
                        loading: false,
                        totalOfferAmount
                    }
                } else {
                    const value = [...current]
                    const totalOfferAmount = value.reduce((acc, item) => acc + (Number(item?.totalAmountPerCodeOrDate) || 0), 0)
                    state.offer[selectedOffer] = {
                        value,
                        count,
                        filterData: { from: date?.from, to: date?.to },
                        offset,
                        loading: false,
                        totalOfferAmount
                    };
                }
            })
            .addCase(offersReport.rejected, (state) => {
                const selectedOffer = state?.selectedOffer
                state.offer[selectedOffer] = { ...state.offer[selectedOffer]!!, loading: false }
                ZenToast({ type: "error", message: "Offer report error" })
            })
    }
})

export const { setOffer, setFilterData } = offerReportSlice?.actions

export default offerReportSlice?.reducer